import { useState, useEffect, useCallback } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { SHIFT_META, SHIFT_TYPES, monthKey, monthLabel } from "@/lib/shifts";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

export default function ShiftCalendar({ settings }) {
  const { t, lang } = useI18n();
  const [cursor, setCursor] = useState(new Date());
  const [days, setDays] = useState([]);
  const [selected, setSelected] = useState(null);

  const mk = monthKey(cursor);

  const load = useCallback(async () => {
    const { data } = await api.get("/shifts", { params: { month: mk } });
    setDays(data.days);
  }, [mk]);

  useEffect(() => { load(); }, [load]);

  const shiftMap = Object.fromEntries(days.map((d) => [d.date, d]));
  const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startWeekday = (firstOfMonth.getDay() + 6) % 7; // Monday-first
  const weekdays = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

  const changeMonth = (delta) =>
    setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + delta, 1));

  const setShift = async (shift) => {
    try {
      await api.put(`/shifts/${selected.date}`, { shift });
      toast.success(t("saved"));
      setSelected(null);
      load();
    } catch { toast.error("Error"); }
  };

  const clearOverride = async () => {
    try {
      await api.delete(`/shifts/${selected.date}`);
      setSelected(null);
      load();
    } catch { toast.error("Error"); }
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-bold">{t("calTitle")}</h2>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-white/10 bg-card px-3 py-2">
        <button data-testid="cal-prev-month" onClick={() => changeMonth(-1)} className="h-9 w-9 rounded-lg hover:bg-secondary flex items-center justify-center">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <span className="font-semibold capitalize" data-testid="cal-month-label">{monthLabel(mk, lang)}</span>
        <button data-testid="cal-next-month" onClick={() => changeMonth(1)} className="h-9 w-9 rounded-lg hover:bg-secondary flex items-center justify-center">
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1.5">
        {weekdays.map((w) => (
          <div key={w} className="text-center text-[11px] uppercase tracking-wide text-muted-foreground py-1">{w}</div>
        ))}
        {Array.from({ length: startWeekday }).map((_, i) => <div key={`e${i}`} />)}
        {days.map((d) => {
          const meta = SHIFT_META[d.shift] || SHIFT_META.off;
          const dayNum = Number(d.date.slice(8, 10));
          const isToday = d.date === todayIso;
          return (
            <button
              key={d.date}
              data-testid={`cal-day-${d.date}`}
              onClick={() => setSelected(d)}
              className="aspect-square rounded-lg border flex flex-col items-center justify-center gap-1 transition-transform hover:-translate-y-0.5"
              style={{
                backgroundColor: `${meta.color}1A`,
                borderColor: isToday ? meta.color : "rgba(255,255,255,0.08)",
                borderWidth: isToday ? 2 : 1,
              }}
            >
              <span className="text-sm font-semibold" style={{ color: meta.color }}>{dayNum}</span>
              <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: meta.color }} />
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        {SHIFT_TYPES.map((s) => (
          <div key={s} className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: SHIFT_META[s].color }} />
            {t(s)}
          </div>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-popover border-white/10 max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display">
              {t("setShift")} {selected?.date}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">{t("editDay")}</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-2.5 mt-2">
            {SHIFT_TYPES.map((s) => {
              const meta = SHIFT_META[s];
              const Icon = meta.icon;
              const active = selected?.shift === s;
              return (
                <button
                  key={s}
                  data-testid={`shift-pick-${s}`}
                  onClick={() => setShift(s)}
                  className="rounded-xl border p-4 flex flex-col items-center gap-2 transition-colors"
                  style={{ borderColor: active ? meta.color : "rgba(255,255,255,0.1)", backgroundColor: active ? `${meta.color}22` : "transparent" }}
                >
                  <Icon className="h-5 w-5" style={{ color: meta.color }} />
                  <span className="text-sm font-semibold">{t(s)}</span>
                </button>
              );
            })}
          </div>
          {selected?.override && (
            <Button variant="outline" data-testid="shift-clear-override" onClick={clearOverride} className="mt-2 border-white/15">
              {t("clearOverride")}
            </Button>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
